'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { isAfter, isFuture } from 'date-fns';
import { useMemo, useState } from 'react';

import CountUp from 'react-countup';
import { Trans, useTranslation } from 'react-i18next';

import { useCurrencyTranslation } from '@/app/hooks/useCurrencyTranslation';
import { formatNumber, formatPriceInCents } from '@/app/utils/formatting';

import { DonationMatching, Milestone } from '@/app/api';
import MilestoneSelector from '@/app/components/MilestoneSelector/MilestoneSelector';
import ProgressBar from '@/app/components/ProgressBar/ProgressBar';
import ProjectTimeLeft from '@/app/components/ProjectTimeLeft/ProjectTimeLeft';
import { COUNT_UP_DURATION_IN_SECONDS } from '@/app/constants';
import { envs } from '@/app/utils/envs';

interface ProjectProgressProps extends PropsWithClassname {
  currentValueInCents: number;
  totalInCents?: number;
  milestones?: Milestone[];
  endsAt?: Date;
  donationsCount?: number;
  matching?: DonationMatching;
  isCurrentAmountPrimaryColor?: boolean;
  testId?: string;
}

const ProjectProgress = ({
  totalInCents,
  currentValueInCents,
  milestones,
  endsAt,
  donationsCount,
  className,
  matching,
  isCurrentAmountPrimaryColor,
  testId,
}: ProjectProgressProps) => {
  const { t } = useTranslation();
  const currencyTranslation = useCurrencyTranslation();

  const getInitialMilestone = () => {
    if (!milestones) {
      return null;
    }

    const higherMilestone = milestones.find((milestone) => milestone.amountInCents > currentValueInCents);

    if (!higherMilestone) {
      return milestones[milestones.length - 1];
    }

    return higherMilestone;
  };

  const [selectedMilestone, setSelectedMilestone] = useState(getInitialMilestone());

  const goal = useMemo(() => {
    if (selectedMilestone?.amountInCents) {
      return selectedMilestone.amountInCents;
    }

    return totalInCents;
  }, [totalInCents, selectedMilestone?.amountInCents]);

  const isMatchingVisible = matching && currentValueInCents > 0 && matching.currentAmountInCents > 0;

  const isDonationCountVisible = useMemo(() => {
    if (goal || isMatchingVisible) {
      return false;
    }

    if (endsAt && !isFuture(endsAt)) {
      return false;
    }

    if (typeof donationsCount !== 'number') {
      return false;
    }

    return true;
  }, [goal, endsAt, donationsCount, isMatchingVisible]);

  return (
    <div className={className} data-testid={testId}>
      {!!milestones?.length && !!selectedMilestone && (
        <MilestoneSelector
          milestones={milestones}
          selectedMilestone={selectedMilestone}
          onSelectMilestone={setSelectedMilestone}
        />
      )}
      {!!goal && <ProgressBar total={goal} currentValue={currentValueInCents} />}
      <div
        className={classNames('flex flex-col gap-[20px] sm:gap-0 sm:flex-row', {
          'mt-[20px]': !!goal,
        })}
      >
        <div className="grow">
          <span className="inline-block text-[20px] whitespace-nowrap tabular-nums">
            {t('components.ProjectProgress.collected')}{' '}
            <span
              data-testid="current-amount"
              className={classNames('font-medium', {
                'text-primary-mid': (!goal || currentValueInCents < goal) && isCurrentAmountPrimaryColor,
              })}
            >
              <Trans i18nKey={'components.ProjectProgress.currentAmount'} values={{ currency: currencyTranslation }}>
                <CountUp
                  start={0}
                  end={currentValueInCents / 100}
                  duration={envs.isInCypressTesting ? 0 : COUNT_UP_DURATION_IN_SECONDS}
                  separator=" "
                />
              </Trans>
            </span>
          </span>
          {!!goal && (
            <span className="block text-[14px] text-neutral-semiTransparent">
              {t('components.ProjectProgress.ofTotal', {
                total: formatPriceInCents(goal),
              })}
            </span>
          )}
          {isDonationCountVisible && (
            <span className="block text-[14px] text-neutral-semiTransparent">
              {t('components.ProjectProgress.donationsCount', {
                count: donationsCount,
                value: formatNumber(donationsCount || 0),
              })}
            </span>
          )}
          {isMatchingVisible && (
            <>
              <span className="block text-[14px] text-neutral-semiTransparent">
                <span>{formatPriceInCents(currentValueInCents - matching.currentAmountInCents)}</span>{' '}
                {t('components.ProjectProgress.donationsCount', {
                  count: donationsCount,
                  value: formatNumber(donationsCount || 0),
                })}
              </span>
              <span className="block text-[14px] text-neutral-semiTransparent">
                {t('components.ProjectProgress.donationFromMatchedCompany', {
                  currentAmountInCents: formatPriceInCents(matching.currentAmountInCents),
                  companyName: matching.name,
                })}
              </span>
            </>
          )}
        </div>
        {!!endsAt && isAfter(endsAt, new Date()) && (
          <ProjectTimeLeft endsAt={endsAt} className="grow-0 flex flex-col sm:items-end" />
        )}
      </div>
    </div>
  );
};

export default ProjectProgress;
